import type { CSSProperties } from "react";
import type { AsyncValueCallback, Link, Path, ValueCallback } from "shelving";
import { BooleanStore, formatURL, getPath } from "shelving";
import { useInstance, useStore } from "shelving/react";
import { useMetadata } from "../context/MetadataContext.js";
import { Loader } from "../loader/Loader.js";
import { closeEventDialog, useDialog } from "./Dialogs.js";
import { Icon } from "./Icon.js";

export interface ButtonProps {
	mods?: string | undefined;
	style?: CSSProperties | undefined;
	children?: React.ReactNode;
	title?: string | undefined;
}

export type ButtonClick = ValueCallback<React.MouseEvent<HTMLButtonElement>>;
export type AsyncButtonClick = AsyncValueCallback<React.MouseEvent<HTMLButtonElement>>;

export function Button({ onClick, children = "Click", mods = "", style, title }: { onClick?: ButtonClick | undefined } & ButtonProps): React.ReactElement {
	return (
		<button //
			type="button"
			className={`button ${mods}`}
			style={style}
			onClick={onClick}
			disabled={!onClick}
			title={title}
		>
			{children}
		</button>
	);
}

/** Output a `<button type="button" className="button">` that has a loading effect when `onClick` callback is processing and prevents double-clicking. */
export function AsyncButton({
	onClick,
	children = "Click",
	mods = "",
	style,
	title,
}: {
	onClick?: AsyncButtonClick | undefined;
} & ButtonProps): React.ReactElement {
	const busy = useStore(useInstance(BooleanStore));
	return (
		<button //
			type="button"
			className={`button ${busy.value ? "effect-waiting" : ""} ${mods}`}
			style={style}
			onClick={onClick ? e => void clickButton(e, onClick, busy) : undefined}
			disabled={!onClick || busy.value}
			title={title}
		>
			{children}
		</button>
	);
}

/** Handle a click on a button. */
export async function clickButton(
	event: React.MouseEvent<HTMLButtonElement>, //
	onClick: AsyncButtonClick,
	busy: BooleanStore,
): Promise<void> {
	if (busy.value) return;
	try {
		busy.set(true);
		await onClick(event);
		closeEventDialog(event);
	} catch (thrown) {
		console.error("BUTTON ERROR:", thrown); // eslint-disable-line no-console
	} finally {
		busy.set(false);
	}
}

export interface PathButtonProps extends ButtonProps {
	path?: Path | undefined;
	disabled?: boolean | undefined;
}

export function PathButton({
	path, //
	disabled = false,
	mods = "",
	style,
	children = path,
	title,
}: PathButtonProps): React.ReactElement {
	const { path: base } = useMetadata();
	const abs = path ? getPath(path, base) : undefined;
	return (
		<a //
			className={`button ${mods} ${!disabled && abs === base ? "button-active" : ""}`}
			href={disabled ? undefined : abs}
			style={style}
			title={title}
		>
			{children}
		</a>
	);
}

export interface LinkButtonProps extends ButtonProps {
	href: Link;
	disabled?: boolean | undefined;
}

export function LinkButton({
	href, //
	disabled = false,
	mods = "",
	style,
	children = formatURL(href),
	title,
}: LinkButtonProps): React.ReactElement {
	return (
		<a //
			className={`button ${mods}`}
			href={disabled ? undefined : href}
			style={style}
			title={title}
		>
			{children}
		</a>
	);
}

/** Button that opens a dialog element. */
export function DialogButton({ dialog, ...props }: { dialog: React.ReactElement | undefined } & ButtonProps): React.ReactElement {
	const showDialog = useDialog();
	return <Button {...props} onClick={dialog && (() => showDialog(dialog))} />;
}

/** Button that opens a modal dialog element. */
export function ModalButton({ modal, ...props }: { modal: React.ReactElement | undefined } & ButtonProps): React.ReactElement {
	const showDialog = useDialog();
	return (
		<Button
			{...props}
			onClick={
				modal &&
				(() =>
					showDialog(
						<aside className="modal">
							<Loader>{modal}</Loader>
						</aside>,
					))
			}
		/>
	);
}

/** Button that opens a menu dialog element. */
export function MenuButton({ menu, children = <Icon icon="more" title="Options" />, ...props }: { menu: React.ReactElement } & ButtonProps): React.ReactElement {
	const showDialog = useDialog();
	return (
		<Button
			{...props}
			onClick={() =>
				showDialog(
					<aside className="modal">
						<Loader>{menu}</Loader>
					</aside>,
				)
			}
		>
			{children}
		</Button>
	);
}
